import React, { useState, useEffect, useContext } from 'react';
import { styled } from '@mui/material/styles';
import { Button, IconButton, Tab, Tabs, Typography, TextField, Tooltip } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import ModalDialog from 'components/ModalDialog/ModalDialog';
import { AppContext } from 'AppContext';
import { fontFamilies, googleFontFaces, loadFontToBrowser, reloadAccountFonts, useAccountFonts } from 'common/fonts';

const PREFIX = 'FontAdd';

const classes = {
  example: `${PREFIX}-example`,
};

const StyledModalDialog = styled(ModalDialog)(({ theme }) => ({
  [`& .${classes.example}`]: {
    fontSize: 16,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const TAB_GOOGLE = 'google';
const TAB_UPLOAD = 'upload';

const https = url => (url || '').replace('http://', 'https://');

const FontAdd = ({ isShown, handleClose, changeFontFamily }) => {
  const appContext = useContext(AppContext);
  const accountFonts = useAccountFonts();
  const [fonts, setFonts] = useState();
  const [tab, setTab] = useState(TAB_GOOGLE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState();

  useEffect(() => {
    const fetchFonts = async () => {
      try {
        const apiKey = import.meta.env.VITE_REACT_APP_GF_API_KEY;
        if (!apiKey) {
          console.error(
            'Google Fonts API key is not configured. Please set VITE_REACT_APP_GF_API_KEY in your environment variables.'
          );
          return;
        }
        let data = await fetch(`https://www.googleapis.com/webfonts/v1/webfonts?key=${apiKey}`);
        data = await data.json();
        setFonts(data);
      } catch (err) {
        console.log(err);
      }
    };
    fetchFonts();
  }, []);

  const [fontPresent, setFontPresent] = useState(null);
  const [file, setFile] = useState(null);

  const handleChange = async e => {
    setError();
    for (let item of fonts.items) {
      if (item.subsets.includes('cyrillic') && item.family.toLowerCase() === e.target.value.toLowerCase()) {
        await loadFontToBrowser(item.family, https(item.files.regular));
        setFontPresent(item);
        return;
      }
    }
    setFontPresent(false);
  };

  const handleFile = e => {
    setError();
    setFile(e.target.files[0]);
  };

  // Сохраняем на сервер все доступные начертания сразу — иначе жирный/курсив пришлось бы
  // добирать из Google в момент печати.
  const addGoogleFont = async () => {
    await appContext.fontApi.addGoogleFonts(googleFontFaces(fontPresent));
    return fontPresent.family;
  };

  const uploadFont = async () => {
    const { data } = await appContext.fontApi.uploadFont(file.name, file);
    if (!data || data.length === 0) {
      throw new Error('Шрифт не найден в файле');
    }
    // В архиве обычно несколько семейств («…», «… Light», «… Bold»). Подставляем в поле
    // базовое — у него самое короткое имя, остальные веса пользователь выберет сам.
    return data.map(font => font.family).sort((a, b) => a.length - b.length)[0];
  };

  const handleOk = () => {
    if (busy) {
      return;
    }
    if (tab === TAB_GOOGLE && !fontPresent) {
      return;
    }
    if (tab === TAB_UPLOAD && !file) {
      return;
    }

    setBusy(true);
    setError();
    (tab === TAB_GOOGLE ? addGoogleFont() : uploadFont())
      .then(family =>
        reloadAccountFonts(appContext.fontApi).then(() => {
          setBusy(false);
          close();
          changeFontFamily(family);
        })
      )
      .catch(err => {
        setBusy(false);
        const message = err.response && err.response.data && err.response.data.message;
        setError(message || 'Не удалось добавить шрифт');
      });
  };

  const deleteFamily = family => {
    if (busy) {
      return;
    }
    setBusy(true);
    setError();
    Promise.all(
      accountFonts.filter(font => font.family === family).map(font => appContext.fontApi.deleteFont(font.fontId))
    )
      .then(() => reloadAccountFonts(appContext.fontApi))
      .then(() => setBusy(false))
      .catch(err => {
        setBusy(false);
        const message = err.response && err.response.data && err.response.data.message;
        setError(message || 'Не удалось удалить шрифт');
      });
  };

  const close = () => {
    setFontPresent(null);
    setFile(null);
    setError();
    handleClose();
  };

  if (!fonts) {
    return null;
  }

  return (
    <StyledModalDialog
      open={isShown}
      noCloseButton
      title='Добавление шрифта'
      handleClose={close}
      okButtonText={busy ? 'Добавляем…' : 'Добавить'}
      handleOk={handleOk}
      size={'sm'}
    >
      <Tabs
        value={tab}
        onChange={(e, value) => {
          setTab(value);
          setError();
        }}
        style={{ marginBottom: 15 }}
      >
        <Tab value={TAB_GOOGLE} label='С fonts.google.com' data-test-id='font-tab-google' />
        <Tab value={TAB_UPLOAD} label='Свой шрифт' data-test-id='font-tab-upload' />
      </Tabs>

      {tab === TAB_GOOGLE ? (
        <>
          <Typography style={{ fontSize: 16 }}>
            Выберите на <a href='https://fonts.google.com/?subset=cyrillic'>fonts.google.com</a> кириллический шрифт,
            который вы хотите добавить и введите его название (например, Montserrat):
          </Typography>
          <TextField
            data-test-id='font-input'
            onChange={handleChange}
            variant='outlined'
            margin='dense'
            style={{ width: '100%', margin: '10px 0 20px 0' }}
          />
          {fontPresent !== null ? (
            fontPresent ? (
              <Typography className={classes.example} style={{ fontFamily: fontPresent.family }}>
                {' '}
                <CheckCircleOutlineIcon style={{ color: '#4CAF50', width: 23, height: 23, marginRight: 5 }} /> Almost
                before we knew it, we had left the ground.
              </Typography>
            ) : (
              <Typography className={classes.example}>
                {' '}
                <HighlightOffIcon style={{ color: '#E83C53', width: 23, height: 23, marginRight: 5 }} /> Шрифт с таким
                названием не найден.
              </Typography>
            )
          ) : (
            ''
          )}
        </>
      ) : (
        <>
          <Typography style={{ fontSize: 16 }}>
            Загрузите файл шрифта (.ttf или .otf) либо zip-архив со всеми начертаниями. Название семейства и начертание
            берутся из самого шрифта. Шрифт будет доступен только в вашем аккаунте.
          </Typography>
          <div style={{ margin: '15px 0 5px 0' }}>
            <Button variant='outlined' component='label' data-test-id='font-upload-button'>
              Выбрать файл
              <input type='file' hidden accept='.zip,.ttf,.otf' onChange={handleFile} data-test-id='font-file-input' />
            </Button>
            <Typography component='span' style={{ marginLeft: 10 }} data-test-id='font-file-name'>
              {file ? file.name : 'файл не выбран'}
            </Typography>
          </div>
        </>
      )}

      {accountFonts.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <Typography variant='h5'>Шрифты аккаунта</Typography>
          {fontFamilies(accountFonts).map(family => (
            <div key={family} style={{ display: 'flex', alignItems: 'center', marginTop: 5 }}>
              <Typography style={{ fontFamily: family, flexGrow: 1 }} data-test-id='font-account-item'>
                {family}
              </Typography>
              <Tooltip title='Удалить шрифт'>
                <IconButton size='small' onClick={() => deleteFamily(family)} data-test-id='font-delete'>
                  <DeleteOutlineIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            </div>
          ))}
        </div>
      )}

      {error && (
        <Typography className={classes.example} style={{ color: '#E83C53', marginTop: 10 }} data-test-id='font-error'>
          {error}
        </Typography>
      )}
    </StyledModalDialog>
  );
};

export default FontAdd;
